/**
 * src/components/public/blog/ShareButtons.tsx
 *
 * Makale detay sayfasında sosyal paylaşım çubuğu — Twitter/X, LinkedIn,
 * Facebook ve "Bağlantıyı Kopyala". Kopyalama clipboard API'sini kullanır;
 * fallback document.execCommand (eski browser).
 *
 * Client Component — clipboard + transient toast state.
 */

'use client'

import { Check, Copy, Facebook, Linkedin, Share2, Twitter } from 'lucide-react'
import { useState } from 'react'

interface ShareButtonsProps {
  url: string
  title: string
  labels: {
    share: string
    twitter: string
    linkedin: string
    facebook: string
    copy: string
    copied: string
  }
  variant?: 'horizontal' | 'vertical'
}

export function ShareButtons({
  url,
  title,
  labels,
  variant = 'horizontal',
}: ShareButtonsProps) {
  const [copied, setCopied] = useState(false)
  const encodedUrl = encodeURIComponent(url)
  const encodedTitle = encodeURIComponent(title)

  const links = [
    {
      key: 'twitter',
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: Twitter,
      label: labels.twitter,
      color: 'hover:text-sky-500',
    },
    {
      key: 'linkedin',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: Linkedin,
      label: labels.linkedin,
      color: 'hover:text-blue-700',
    },
    {
      key: 'facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: Facebook,
      label: labels.facebook,
      color: 'hover:text-blue-600',
    },
  ] as const

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API yok — kullanıcıya manuel kopya için input sun
      const ta = document.createElement('textarea')
      ta.value = url
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.focus()
      ta.select()
      try {
        document.execCommand('copy')
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
      } catch {
        /* sessizce geç */
      }
      document.body.removeChild(ta)
    }
  }

  const isVertical = variant === 'vertical'

  return (
    <div
      className={`flex items-center gap-1 ${
        isVertical ? 'flex-col' : 'flex-row flex-wrap'
      }`}
      role="group"
      aria-label={labels.share}
    >
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 ${
          isVertical ? 'mb-2' : 'mr-2'
        }`}
      >
        <Share2 size={12} aria-hidden />
        {labels.share}
      </span>
      {links.map((l) => (
        <a
          key={l.key}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={l.label}
          title={l.label}
          className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-400 ${l.color} dark:hover:text-white hover:border-current transition-colors`}
        >
          <l.icon size={16} aria-hidden />
        </a>
      ))}
      <button
        type="button"
        onClick={copyToClipboard}
        aria-label={copied ? labels.copied : labels.copy}
        title={copied ? labels.copied : labels.copy}
        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${
          copied
            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400'
            : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-400 hover:border-brand-500 hover:text-brand-600 dark:hover:text-brand-300'
        }`}
      >
        {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
      </button>
    </div>
  )
}
